/******************************************************************************
 * Created Date: Friday September 11th 2020                                   *
 * Author: Sean W.                                                            *
 * -----                                                                      *
 * Last Modified: Fri Sep 18 2020                                             *
 * Modified By: Sean W.                                                       *
 * -----                                                                      *
 * File: /src/components/algorithms/frames/processImagesModal/form2/SwirCarousel.js*
 * Copyright (c) 2020 MultiSensor                                             *
 ******************************************************************************/

import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Button, MobileStepper, Paper, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import React from "react";
import SwipeableViews from "react-swipeable-views";

import AlgorithmsAPI from "../../../../../apis/AlgorithmsAPI";

const PREFIX = "SwirCarousel";

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  img: `${PREFIX}-img`
};

const Root = styled("div")(({ theme }) => ({
  [`.${classes.root}`]: {
    maxWidth: 400,
    flexGrow: 1
  },

  [`.${classes.header}`]: {
    display: "flex",
    alignItems: "center",
    height: 50,
    paddingLeft: theme.spacing(4),
    backgroundColor: theme.palette.background.default
  },

  [`.${classes.img}`]: {
    height: 255,
    display: "block",
    maxWidth: 400,
    overflow: "hidden",
    width: "100%"
  }
}));

const SwirCarousel = ({ swirs }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = swirs.length;

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleStepChange = step => {
    setActiveStep(step);
  };

  return (
    <Root className={classes.root}>
      <Paper square elevation={0} className={classes.header}>
        <Typography>{swirs[activeStep].fileName}</Typography>
      </Paper>
      <SwipeableViews
        axis={theme.direction === "rtl" ? "x-reverse" : "x"}
        index={activeStep}
        onChangeIndex={handleStepChange}
        enableMouseEvents
      >
        {swirs.map((frame, index) => (
          <div key={frame.fileName}>
            {Math.abs(activeStep - index) <= 2 && (
              <img
                className={classes.img}
                src={`${AlgorithmsAPI.baseURL}/jpg/${frame.container}/${frame.fileName}.jpg`}
                alt={frame.fileName}
              />
            )}
          </div>
        ))}
      </SwipeableViews>
      <MobileStepper
        steps={maxSteps}
        position="static"
        variant="text"
        activeStep={activeStep}
        nextButton={
          <Button
            size="small"
            onClick={handleNext}
            disabled={activeStep === maxSteps - 1}
          >
            Next
            {theme.direction === "rtl" ? (
              <KeyboardArrowLeft />
            ) : (
              <KeyboardArrowRight />
            )}
          </Button>
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            {theme.direction === "rtl" ? (
              <KeyboardArrowRight />
            ) : (
              <KeyboardArrowLeft />
            )}
            Back
          </Button>
        }
      />
    </Root>
  );
};

SwirCarousel.propTypes = {
  swirs: PropTypes.array.isRequired
};

export default SwirCarousel;
